/**
 * Phase 19.34 — Cookie consent banner.
 *
 * Minimal GDPR/PIPEDA-compliant banner. Business Genie only uses *essential*
 * cookies (session JWT, CSRF, preference cookies) — no advertising or third-
 * party tracking — so the banner is a simple "we use essential cookies"
 * acknowledgement rather than a full granular-consent manager.
 *
 * Once acknowledged, a localStorage flag suppresses the banner on subsequent
 * visits. Users can revisit /legal/privacy to read the cookie policy in full.
 */
import React, { useEffect, useState } from "react";
import { Cookie, X } from "lucide-react";

const LS_KEY = "bg_cookie_consent_v1";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (!localStorage.getItem(LS_KEY)) {
        setVisible(true);
      }
    } catch (_e) {
      // localStorage unavailable (private mode, sandboxed iframe, etc.) — just show.
      setVisible(true);
    }
  }, []);

  const accept = () => {
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({ at: new Date().toISOString(), v: 1 }));
    } catch (_e) {
      // ignore
    }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie notice"
      data-testid="cookie-banner"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-md z-[9999] bg-slate-900 text-slate-100 border border-slate-700 rounded-xl shadow-2xl p-4 backdrop-blur-md"
    >
      <div className="flex items-start gap-3">
        <Cookie className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold mb-1">We use essential cookies only</p>
          <p className="text-xs text-slate-300 leading-relaxed">
            Business Genie uses cookies that are strictly necessary to keep you
            signed in and protect against attacks. We do <strong>not</strong>{" "}
            use advertising or tracking cookies. See our{" "}
            <a
              href="/legal/privacy"
              className="underline text-cyan-300 hover:text-cyan-200"
              data-testid="cookie-banner-privacy-link"
            >
              Privacy Policy
            </a>{" "}
            for details.
          </p>
          <div className="flex gap-2 mt-3">
            <button
              onClick={accept}
              data-testid="cookie-banner-accept-btn"
              className="px-3 py-1.5 text-xs font-medium bg-emerald-500 hover:bg-emerald-400 text-slate-900 rounded-md transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
        <button
          onClick={accept}
          aria-label="Dismiss cookie notice"
          data-testid="cookie-banner-close-btn"
          className="text-slate-400 hover:text-slate-200 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
